import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, LogOut, ArrowLeft, Save, Calendar, Users, Heart, CreditCard } from "lucide-react";
import { format } from "date-fns";
import WaiverConsentModal from "@/components/WaiverConsentModal";
import PayPalCheckout from "@/components/PayPalCheckout";
import StripeCheckoutButton from "@/components/StripeCheckoutButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authed = (extra = {}) => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("ca_parent_token") || ""}`, ...extra },
});

const ParentDashboard = () => {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [children, setChildren] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);
  const [payingBooking, setPayingBooking] = useState(null); // { id, amount, purpose }

  const parentToken = localStorage.getItem("ca_parent_token") || "";

  const load = async () => {
    try {
      const [{ data: p }, { data: c }, { data: b }] = await Promise.all([
        axios.get(`${API}/portal/parent/me`, authed()),
        axios.get(`${API}/portal/parent/children`, authed()),
        axios.get(`${API}/portal/parent/bookings`, authed()),
      ]);
      setMe(p);
      setChildren(c);
      setBookings(b);
      if (!p?.waiver_accepted) {
        setShowWaiver(true);
      }
    } catch (e) {
      if (e?.response?.status === 401) { nav("/portal/login"); return; }
      toast.error("Could not load your account.");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("ca_parent_token")) { nav("/portal/login"); return; }
    setBusy(true); load().finally(() => setBusy(false));
  }, []);  // eslint-disable-line

  const logout = () => { localStorage.removeItem("ca_parent_token"); nav("/portal/login"); };

  const saveParent = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/portal/parent/me`, {
        photo_url: me.photo_url || "",
        notes: me.notes || "",
      }, authed({ "Content-Type": "application/json" }));
      setMe(data);
      toast.success("Profile saved.");
    } catch (e) {
      toast.error("Save failed.");
    } finally { setSaving(false); }
  };

  const saveChild = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/portal/parent/children/${id}`, patch, authed({ "Content-Type": "application/json" }));
      setChildren((cs) => cs.map((c) => (c.id === id ? data : c)));
      toast.success(`${data.first_name} updated.`);
    } catch { toast.error("Could not update child."); }
  };

  if (busy || !me) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  return (
    <main data-testid="parent-portal" className="min-h-screen bg-background pb-24">
      <div className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif-display">C</span>
            <span className="font-serif-display text-lg">Care Anywhere · Parent portal</span>
          </Link>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="parent-logout" className="rounded-full">
            <LogOut size={14} className="mr-1.5" /> Sign out
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Welcome</div>
        <h1 className="mt-3 font-serif-display text-3xl md:text-4xl">Hi {me.first_name}.</h1>
        <p className="mt-2 text-muted-foreground text-sm">Manage your bookings, children, and care preferences in one place.</p>

        {/* Bookings */}
        <section data-testid="section-bookings" className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={16} className="text-primary" />
            <h2 className="font-serif-display text-2xl">Your bookings</h2>
          </div>
          <div className="space-y-3">
            {bookings.length === 0 && (
              <div className="rounded-2xl border border-border/70 bg-muted/40 p-6 text-sm text-muted-foreground">No bookings yet.</div>
            )}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="rounded-2xl border border-border/70 bg-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="font-serif-display text-xl text-foreground">{b.event?.name || "Booking"}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {b.event?.venue} · {b.event?.start_at ? format(new Date(b.event.start_at), "EEEE, MMMM d 'at' p") : ""}
                  </div>
                  {b.payment_status && (
                    <Badge variant={b.payment_status === "paid" ? "default" : "secondary"} className="mt-2 text-[10px]" data-testid={`booking-payment-${b.id}`}>
                      {b.payment_status === "paid" ? "Paid in full" : "Deposit received"}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 self-start md:self-center">
                  <Badge variant={b.status === "confirmed" ? "default" : "secondary"}>{b.status}</Badge>
                  {b.payment_status !== "paid" && (
                    <Button
                      data-testid={`booking-pay-${b.id}`}
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => setPayingBooking({ id: b.id, amount: b.payment_status === "deposit_paid" ? 250 : 150, purpose: b.payment_status === "deposit_paid" ? "booking_full" : "booking_deposit", event_name: b.event?.name })}
                    >
                      <CreditCard size={13} className="mr-1.5" />
                      {b.payment_status === "deposit_paid" ? "Pay balance" : "Reserve"}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Parent profile */}
        <section data-testid="section-me" className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} className="text-primary" />
            <h2 className="font-serif-display text-2xl">Your details</h2>
          </div>
          <div className="rounded-2xl border border-border/70 bg-card p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
                <Input value={`${me.first_name} ${me.last_name}`} readOnly className="mt-2 h-11 rounded-lg bg-muted" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
                <Input value={me.phone || ""} readOnly className="mt-2 h-11 rounded-lg bg-muted" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Your photo URL (for door verification)</Label>
                <Input data-testid="parent-photo-url" value={me.photo_url || ""} onChange={(e) => setMe({ ...me, photo_url: e.target.value })} placeholder="https://…" className="mt-2 h-11 rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes for the care team</Label>
                <Textarea data-testid="parent-notes" value={me.notes || ""} onChange={(e) => setMe({ ...me, notes: e.target.value })} rows={3} className="mt-2 rounded-lg resize-none" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button data-testid="parent-save-me" onClick={saveParent} disabled={saving} className="rounded-full bg-primary text-primary-foreground">
                <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </section>

        {/* Children */}
        <section data-testid="section-children" className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-primary" />
            <h2 className="font-serif-display text-2xl">Your children</h2>
          </div>
          {children.length === 0 && (
            <div className="rounded-2xl border border-border/70 bg-muted/40 p-6 text-sm text-muted-foreground">No children on file yet. Your caregiver will add them at first booking.</div>
          )}
          <div className="space-y-4">
            {children.map((c, idx) => (
              <ChildCard key={c.id} idx={idx} child={c} onSave={(patch) => saveChild(c.id, patch)} />
            ))}
          </div>
        </section>
      </div>

      {showWaiver && (
        <WaiverConsentModal
          parentToken={parentToken}
          onDone={(updated) => { setMe(updated); setShowWaiver(false); }}
          onClose={() => setShowWaiver(false)}
        />
      )}

      {payingBooking && (
        <div data-testid="pay-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-background border border-border p-6 shadow-2xl">
            <div className="text-xs tracking-[0.25em] uppercase text-primary/80 mb-1">
              {payingBooking.purpose === "booking_deposit" ? "Reserve your date" : "Pay outstanding balance"}
            </div>
            <div className="font-serif-display text-2xl">{payingBooking.event_name || "Care Anywhere booking"}</div>
            <div className="mt-2 text-sm text-muted-foreground">Amount due: <span className="font-medium text-foreground">${payingBooking.amount.toFixed(2)}</span></div>

            {/* Stripe — recommended (Apple Pay, Google Pay, cards, saved cards) */}
            <div className="mt-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-primary/80 mb-2">Fastest — card, Apple Pay, Google Pay</div>
              <StripeCheckoutButton
                lookupKey={
                  payingBooking.purpose === "booking_deposit"
                    ? (payingBooking.amount >= 500 ? "deposit_500" : "deposit_250")
                    : (payingBooking.amount >= 2500 ? "balance_2500" : payingBooking.amount >= 1000 ? "balance_1000" : "balance_500")
                }
                purpose={payingBooking.purpose}
                bookingId={payingBooking.id}
                parentToken={parentToken}
                amountLabel={`$${payingBooking.amount.toFixed(2)}`}
                fullWidth
              />
            </div>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* PayPal */}
            <div>
              <PayPalCheckout
                amountUsd={payingBooking.amount}
                purpose={payingBooking.purpose}
                bookingId={payingBooking.id}
                description={`${payingBooking.event_name} — ${payingBooking.purpose.replace("_", " ")}`}
                parentToken={parentToken}
                onSuccess={() => { setPayingBooking(null); load(); }}
              />
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={() => setPayingBooking(null)} data-testid="pay-close" className="rounded-full">Close</Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

const ChildCard = ({ idx, child, onSave }) => {
  const [c, setC] = useState(child);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    await onSave({
      photo_url: c.photo_url || "",
      allergies: c.allergies || "",
      medical_notes: c.medical_notes || "",
      soothing_techniques: c.soothing_techniques || "",
      favorite_activities: c.favorite_activities || "",
    });
    setSaving(false);
  };

  return (
    <div data-testid={`child-card-${idx}`} className="rounded-2xl border border-border/70 bg-card p-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-accent/20 flex items-center justify-center">
          <Heart size={16} className="text-primary" />
        </div>
        <div>
          <div className="font-serif-display text-xl">{c.first_name}</div>
          <div className="text-xs text-muted-foreground">Age {c.age_years}</div>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Photo URL</Label>
          <Input data-testid={`child-photo-${idx}`} value={c.photo_url || ""} onChange={(e) => set("photo_url", e.target.value)} className="mt-2 h-11 rounded-lg" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Allergies / medical alerts</Label>
          <Input data-testid={`child-allergies-${idx}`} value={c.allergies || ""} onChange={(e) => set("allergies", e.target.value)} className="mt-2 h-11 rounded-lg" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Medical notes</Label>
          <Input data-testid={`child-medical-${idx}`} value={c.medical_notes || ""} onChange={(e) => set("medical_notes", e.target.value)} className="mt-2 h-11 rounded-lg" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Soothing techniques</Label>
          <Textarea data-testid={`child-soothing-${idx}`} rows={2} value={c.soothing_techniques || ""} onChange={(e) => set("soothing_techniques", e.target.value)} className="mt-2 rounded-lg resize-none" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Favorite activities</Label>
          <Textarea data-testid={`child-fav-${idx}`} rows={2} value={c.favorite_activities || ""} onChange={(e) => set("favorite_activities", e.target.value)} className="mt-2 rounded-lg resize-none" />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button data-testid={`child-save-${idx}`} onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : `Save ${c.first_name}`}
        </Button>
      </div>
    </div>
  );
};

export default ParentDashboard;
